import { FormEvent, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Activity,
  BarChart3,
  Coins,
  DatabaseBackup,
  HardDrive,
  Download,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  Pencil,
  RefreshCw,
  Search,
  Settings2,
  ShieldCheck,
  Trash2,
  Upload,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import AdminObservabilityPanel from '@/pages/admin/AdminObservabilityPanel'
import AdminUsageDashboard from '@/pages/admin/AdminUsageDashboard'
import { clearAdminToken, getAdminToken, setAdminToken, verifyAdminToken } from '@/api/adminApi'
import {
  deleteAdminBackup,
  downloadAdminBackup,
  getBackupConfig,
  getEncStatus,
  listAdminBackups,
  manualRunBackup,
  type AdminBackupRemoteStatus,
  type AdminBackupTarget,
  putBackupConfig,
  normalizeAdminBackupTarget,
  setBackupPassphrase,
  restartAdminServer,
  restoreAdminBackup,
  uploadAdminRestore,
  type BackupType,
  type AdminBackupFile,
} from '@/api/adminBackupApi'
import { getStorageConfig } from '@/api/storageConfigApi'
import {
  listAdminUsers,
  patchAdminUser,
  resetAdminUserPassword,
  topUpAdminCredits,
  type AdminUser,
  type PatchAdminUserBody,
} from '@/api/adminUsersApi'
import { ApiError, friendlyMessage } from '@/api/http'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'

const DEFAULT_LIMIT = 20
const ADMIN_VERIFY_QUERY_KEY = 'admin-token-verify'
const ADMIN_USERS_QUERY_KEY = 'admin-users'
const ADMIN_BACKUPS_QUERY_KEY = 'admin-backups'
const ADMIN_BACKUP_CONFIG_QUERY_KEY = 'admin-backup-config'
const ADMIN_BACKUP_ENC_STATUS_QUERY_KEY = 'admin-backup-enc-status'
const ADMIN_STORAGE_CONFIG_QUERY_KEY = 'admin-storage-config'
const TOPUP_USER_SEARCH_QUERY_KEY = 'admin-topup-user-search'
const BACKUP_TYPE_OPTIONS = ['local', 's3'] as const

interface EditUserState {
  user: AdminUser
  trustLevel: string
  isAdmin: boolean
  isActive: boolean
}

interface BackupTypeFormState {
  enabled: boolean
  interval: string
  keepN: string
  target: AdminBackupTarget
}

function formatCreatedAt(value: number): string {
  if (!value) return '-'
  const millis = value > 10_000_000_000 ? value : value * 1000
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(millis))
}

function getAdminErrorMessage(error: unknown, t: ReturnType<typeof useTranslation>['t']): string {
  if (error instanceof ApiError) {
    if (error.code === 'last_admin_protected' || error.message === 'last_admin_protected') {
      return t('adminConsole.users.errors.lastAdminProtected')
    }
    if (error.code === 'user_not_found' || error.message === 'user_not_found' || error.status === 404) {
      return t('adminConsole.users.errors.userNotFound')
    }
  }
  return friendlyMessage(error, t('common.serviceError'))
}

function createTopUpIdempotencyKey(): string {
  return `admin-topup-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

function formatBytes(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let size = value
  let unit = 0
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024
    unit += 1
  }
  return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: unit === 0 ? 0 : 1 }).format(size)} ${units[unit]}`
}

function inferRestoreTypeFromFileName(fileName: string): BackupType {
  return fileName.toLowerCase().includes('_assets_') ? 'assets' : 'core'
}

export default function AdminSettings() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [token, setToken] = useState('')
  const [showToken, setShowToken] = useState(false)
  const [hasToken, setHasToken] = useState(() => Boolean(getAdminToken()))
  const [tokenRevision, setTokenRevision] = useState(0)
  const [searchInput, setSearchInput] = useState('')
  const [query, setQuery] = useState('')
  const [limit, setLimit] = useState(DEFAULT_LIMIT)
  const [offset, setOffset] = useState(0)
  const [editState, setEditState] = useState<EditUserState | null>(null)
  const [resetUser, setResetUser] = useState<AdminUser | null>(null)
  const [newPassword, setNewPassword] = useState('')
  const [topUpUserId, setTopUpUserId] = useState('')
  const [topUpUserQuery, setTopUpUserQuery] = useState('')
  const [topUpUserSearchOpen, setTopUpUserSearchOpen] = useState(false)
  const [selectedTopUpUser, setSelectedTopUpUser] = useState<AdminUser | null>(null)
  const [topUpAmount, setTopUpAmount] = useState('')
  const [topUpNote, setTopUpNote] = useState('')
  const [restoreType, setRestoreType] = useState<BackupType>('core')
  const [passphrase, setPassphrase] = useState('')
  const [coreConfig, setCoreConfig] = useState<BackupTypeFormState>({
    enabled: false,
    interval: '',
    keepN: '',
    target: 'local',
  })
  const [assetsConfig, setAssetsConfig] = useState<BackupTypeFormState>({
    enabled: false,
    interval: '',
    keepN: '',
    target: 'local',
  })
  const [savingType, setSavingType] = useState<BackupType | null>(null)
  const [runningType, setRunningType] = useState<BackupType | null>(null)
  const [restoreTarget, setRestoreTarget] = useState<AdminBackupFile | null>(null)
  const [restoreUploadFile, setRestoreUploadFile] = useState<File | null>(null)
  const [restoreConfirm, setRestoreConfirm] = useState('')
  const [allowSelfRestart, setAllowSelfRestart] = useState(false)

  const verifyQuery = useQuery({
    queryKey: [ADMIN_VERIFY_QUERY_KEY, tokenRevision],
    queryFn: verifyAdminToken,
    enabled: hasToken,
    retry: false,
  })
  const adminAccessAllowed = hasToken && verifyQuery.data === true
  const tokenStatus = !hasToken
    ? { variant: 'secondary' as const, label: t('admin.tokenMissing'), loading: false }
    : verifyQuery.isLoading || verifyQuery.isFetching
      ? { variant: 'secondary' as const, label: t('admin.tokenVerifying'), loading: true }
      : adminAccessAllowed
        ? { variant: 'default' as const, label: t('admin.tokenConfigured'), loading: false }
        : { variant: 'destructive' as const, label: t('admin.tokenInvalid'), loading: false }

  const usersQuery = useQuery({
    queryKey: [ADMIN_USERS_QUERY_KEY, limit, offset, query],
    queryFn: () => listAdminUsers({ limit, offset, q: query }),
    enabled: adminAccessAllowed,
  })

  useEffect(() => {
    const userQuery = topUpUserId.trim()
    if (userQuery.length < 2) {
      setTopUpUserQuery('')
      return
    }
    const timer = window.setTimeout(() => setTopUpUserQuery(userQuery), 300)
    return () => window.clearTimeout(timer)
  }, [topUpUserId])

  const topUpUserSearchQuery = useQuery({
    queryKey: [TOPUP_USER_SEARCH_QUERY_KEY, topUpUserQuery],
    queryFn: () => listAdminUsers({ limit: 8, offset: 0, q: topUpUserQuery }),
    enabled: adminAccessAllowed && topUpUserSearchOpen && topUpUserQuery.length >= 2 && !selectedTopUpUser,
  })

  const coreBackupsQuery = useQuery({
    queryKey: [ADMIN_BACKUPS_QUERY_KEY, 'core'],
    queryFn: () => listAdminBackups('core'),
    enabled: adminAccessAllowed,
  })
  const assetsBackupsQuery = useQuery({
    queryKey: [ADMIN_BACKUPS_QUERY_KEY, 'assets'],
    queryFn: () => listAdminBackups('assets'),
    enabled: adminAccessAllowed,
  })
  const backupConfigQuery = useQuery({
    queryKey: [ADMIN_BACKUP_CONFIG_QUERY_KEY],
    queryFn: getBackupConfig,
    enabled: adminAccessAllowed,
  })
  const encStatusQuery = useQuery({
    queryKey: [ADMIN_BACKUP_ENC_STATUS_QUERY_KEY],
    queryFn: getEncStatus,
    enabled: adminAccessAllowed,
  })
  const storageConfigQuery = useQuery({
    queryKey: [ADMIN_STORAGE_CONFIG_QUERY_KEY],
    queryFn: getStorageConfig,
    enabled: adminAccessAllowed,
  })

  const backupConfigs = backupConfigQuery.data
  const storageBackend = storageConfigQuery.data?.backend.trim().toLowerCase()
  const isS3Storage = storageBackend === 's3'
  const storageBackendLabel = storageBackend === 's3'
    ? t('storageConfig.backends.s3')
    : storageBackend === 'local'
      ? t('storageConfig.backends.local')
      : t('adminConsole.backup.storageBackendUnknown')
  const passphraseSet =
    typeof backupConfigs?.passphrase_set === 'boolean'
      ? backupConfigs.passphrase_set
      : typeof encStatusQuery.data?.passphrase_set === 'boolean'
        ? encStatusQuery.data.passphrase_set
        : false

  useEffect(() => {
    if (!backupConfigs) return
    const { core, assets } = backupConfigs.configs
    setCoreConfig({
      enabled: core.effective.enabled,
      interval: core.effective.interval,
      keepN: String(core.effective.keep_n),
      target: normalizeAdminBackupTarget(core.effective.target),
    })
    setAssetsConfig({
      enabled: assets.effective.enabled,
      interval: assets.effective.interval,
      keepN: String(assets.effective.keep_n),
      target: normalizeAdminBackupTarget(assets.effective.target),
    })
  }, [backupConfigs])

  const users = usersQuery.data?.users ?? []
  const topUpUserResults = topUpUserSearchQuery.data?.users ?? []
  const coreBackups = coreBackupsQuery.data?.backups ?? []
  const assetsBackups = assetsBackupsQuery.data?.backups ?? []
  const isTopUpUserSearchPending = topUpUserId.trim().length >= 2 && topUpUserId.trim() !== topUpUserQuery
  const hasPreviousPage = offset > 0
  const hasNextPage = users.length >= limit

  const currentRange = useMemo(() => {
    if (!users.length) return t('adminConsole.users.emptyRange')
    if (users.length === 1) return t('adminConsole.users.single', { index: offset + 1 })
    return t('adminConsole.users.range', {
      start: offset + 1,
      end: offset + users.length,
    })
  }, [offset, t, users.length])

  const invalidateUsers = () => {
    queryClient.invalidateQueries({ queryKey: [ADMIN_USERS_QUERY_KEY] })
  }

  const updateUserMutation = useMutation({
    mutationFn: ({ userId, body }: { userId: string; body: PatchAdminUserBody }) => patchAdminUser(userId, body),
    onSuccess: () => {
      toast.success(t('adminConsole.users.updateSuccess'))
      setEditState(null)
      invalidateUsers()
    },
    onError: (error) => {
      toast.error(getAdminErrorMessage(error, t))
    },
  })

  const resetPasswordMutation = useMutation({
    mutationFn: ({ userId, password }: { userId: string; password: string }) =>
      resetAdminUserPassword(userId, { new_password: password }),
    onSuccess: (response) => {
      toast.success(t('adminConsole.users.resetSuccess', { count: response.deleted_sessions }))
      setResetUser(null)
      setNewPassword('')
    },
    onError: (error) => {
      toast.error(getAdminErrorMessage(error, t))
    },
  })

  const topUpMutation = useMutation({
    mutationFn: () =>
      topUpAdminCredits({
        user_id: selectedTopUpUser?.user_id ?? topUpUserId.trim(),
        amount: Number(topUpAmount),
        idempotency_key: createTopUpIdempotencyKey(),
        ...(topUpNote.trim() ? { note: topUpNote.trim() } : {}),
      }),
    onSuccess: () => {
      toast.success(t('adminConsole.credits.topUpSuccess'))
      setTopUpAmount('')
      setTopUpNote('')
    },
    onError: (error) => {
      toast.error(friendlyMessage(error, t('common.serviceError')))
    },
  })

  const passphraseMutation = useMutation({
    mutationFn: () => setBackupPassphrase(passphrase.trim()),
    onSuccess: () => {
      setPassphrase('')
      toast.success(t('adminConsole.backup.passphraseSaved'))
      queryClient.invalidateQueries({ queryKey: [ADMIN_BACKUP_CONFIG_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [ADMIN_BACKUP_ENC_STATUS_QUERY_KEY] })
    },
    onError: (error) => {
      toast.error(getAdminErrorMessage(error, t))
    },
  })

  const saveBackupConfigMutation = useMutation({
    mutationFn: ({ type, payload }: { type: BackupType; payload: { enabled: boolean; interval: string; keep_n: number; target: AdminBackupTarget } }) =>
      putBackupConfig(type, payload),
    onMutate: ({ type }) => {
      setSavingType(type)
    },
    onSuccess: () => {
      toast.success(t('adminConsole.backup.configSaved'))
      queryClient.invalidateQueries({ queryKey: [ADMIN_BACKUP_CONFIG_QUERY_KEY] })
    },
    onError: (error) => {
      toast.error(getAdminErrorMessage(error, t))
    },
    onSettled: () => {
      setSavingType(null)
    },
  })

  const manualRunMutation = useMutation({
    mutationFn: ({ type, label }: { type: BackupType; label?: string }) => manualRunBackup(type, label),
    onMutate: ({ type }) => {
      setRunningType(type)
    },
    onSuccess: (response, variables) => {
      toast.success(t('adminConsole.backup.runSuccess', { name: response.backup.name }))
      queryClient.invalidateQueries({ queryKey: [ADMIN_BACKUPS_QUERY_KEY, variables.type] })
    },
    onError: (error) => {
      toast.error(getAdminErrorMessage(error, t))
    },
    onSettled: () => {
      setRunningType(null)
    },
  })

  const downloadBackupMutation = useMutation({
    mutationFn: downloadAdminBackup,
    onError: (error) => {
      toast.error(getAdminErrorMessage(error, t))
    },
  })

  const deleteBackupMutation = useMutation({
    mutationFn: deleteAdminBackup,
    onSuccess: () => {
      toast.success(t('adminConsole.backup.deleteSuccess'))
      queryClient.invalidateQueries({ queryKey: [ADMIN_BACKUPS_QUERY_KEY] })
    },
    onError: (error) => {
      toast.error(getAdminErrorMessage(error, t))
    },
  })

  const restoreMutation = useMutation({
    mutationFn: async () => {
      if (restoreUploadFile) return uploadAdminRestore(restoreUploadFile, restoreConfirm)
      if (!restoreTarget) throw new Error(t('adminConsole.backup.restoreMissing'))
      return restoreAdminBackup(restoreTarget.name, restoreConfirm, restoreType)
    },
    onSuccess: (response) => {
      setAllowSelfRestart(response.allow_self_restart)
      setRestoreTarget(null)
      setRestoreUploadFile(null)
      setRestoreConfirm('')
      toast.success(t('adminConsole.backup.restoreStaged'))
      setRestoreType('core')
      queryClient.invalidateQueries({ queryKey: [ADMIN_BACKUPS_QUERY_KEY] })
    },
    onError: (error) => {
      toast.error(getAdminErrorMessage(error, t))
    },
  })

  const restartMutation = useMutation({
    mutationFn: restartAdminServer,
    onSuccess: () => {
      toast.success(t('adminConsole.backup.restartRequested'))
    },
    onError: (error) => {
      toast.error(getAdminErrorMessage(error, t))
    },
  })

  const onSave = () => {
    const nextToken = token.trim()
    if (!nextToken) {
      toast.error(t('admin.tokenRequired'))
      return
    }
    setAdminToken(nextToken)
    setToken('')
    setShowToken(false)
    setHasToken(true)
    setTokenRevision((value) => value + 1)
    toast.success(t('admin.tokenSaved'))
  }

  const onClear = () => {
    clearAdminToken()
    setToken('')
    setShowToken(false)
    setHasToken(false)
    setTokenRevision((value) => value + 1)
    toast.success(t('admin.tokenCleared'))
    queryClient.removeQueries({ queryKey: [ADMIN_VERIFY_QUERY_KEY] })
    queryClient.removeQueries({ queryKey: [ADMIN_USERS_QUERY_KEY] })
    queryClient.removeQueries({ queryKey: [ADMIN_BACKUPS_QUERY_KEY] })
    queryClient.removeQueries({ queryKey: [ADMIN_BACKUP_CONFIG_QUERY_KEY] })
    queryClient.removeQueries({ queryKey: [ADMIN_BACKUP_ENC_STATUS_QUERY_KEY] })
    queryClient.removeQueries({ queryKey: [ADMIN_STORAGE_CONFIG_QUERY_KEY] })
  }

  const onSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setOffset(0)
    setQuery(searchInput.trim())
  }

  const getBackupRemoteStatus = (type: BackupType): AdminBackupRemoteStatus | undefined => {
    return backupConfigs?.remote[type]
  }

  const savePassphrase = () => {
    if (!passphrase.trim()) {
      toast.error(t('adminConsole.backup.passphraseRequired'))
      return
    }
    passphraseMutation.mutate()
  }

  const saveBackupConfig = (type: BackupType, values: BackupTypeFormState) => {
    const interval = values.interval.trim()
    const keepN = Number(values.keepN)
    if (!interval) {
      toast.error(t('adminConsole.backup.intervalRequired'))
      return
    }
    if (!Number.isFinite(keepN) || keepN <= 0 || !Number.isInteger(keepN)) {
      toast.error(t('adminConsole.backup.keepNPositiveInteger'))
      return
    }
    saveBackupConfigMutation.mutate({
      type,
      payload: {
        enabled: values.enabled,
        interval,
        keep_n: keepN,
        target: values.target,
      },
    })
  }

  const runBackup = (type: BackupType) => {
    manualRunMutation.mutate({ type })
  }

  const getRemoteBadgeVariant = (status: string) => {
    return status === 'degraded' ? 'destructive' : 'secondary'
  }

  const getRemoteBadgeLabel = (status: string) => {
    if (status === 'ok') return t('adminConsole.backup.remoteHealthy')
    if (status === 'degraded') return t('adminConsole.backup.remoteDegraded')
    return status || t('adminConsole.backup.remoteNotConfigured')
  }

  const openEditDialog = (user: AdminUser) => {
    setEditState({
      user,
      trustLevel: String(user.trust_level),
      isAdmin: user.is_admin,
      isActive: user.is_active,
    })
  }

  const submitEdit = () => {
    if (!editState) return
    const trustLevel = Number(editState.trustLevel)
    if (!Number.isInteger(trustLevel) || trustLevel < 0) {
      toast.error(t('adminConsole.users.errors.invalidTrustLevel'))
      return
    }
    updateUserMutation.mutate({
      userId: editState.user.user_id,
      body: {
        trust_level: trustLevel,
        is_admin: editState.isAdmin,
        is_active: editState.isActive,
      },
    })
  }

  const submitResetPassword = () => {
    if (!resetUser) return
    const password = newPassword.trim()
    if (password.length < 8) {
      toast.error(t('adminConsole.users.errors.passwordTooShort'))
      return
    }
    resetPasswordMutation.mutate({ userId: resetUser.user_id, password })
  }

  const onTopUp = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const amount = Number(topUpAmount)
    const userID = selectedTopUpUser?.user_id ?? topUpUserId.trim()
    if (!userID) {
      toast.error(t('adminConsole.credits.errors.userRequired'))
      return
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error(t('adminConsole.credits.errors.amountInvalid'))
      return
    }
    topUpMutation.mutate()
  }

  const onTopUpUserInputChange = (value: string) => {
    setTopUpUserId(value)
    setSelectedTopUpUser(null)
    setTopUpUserSearchOpen(true)
  }

  const onSelectTopUpUser = (user: AdminUser) => {
    setSelectedTopUpUser(user)
    setTopUpUserId(user.user_id)
    setTopUpUserSearchOpen(false)
  }

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-8 sm:px-6">
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-semibold tracking-normal">{t('admin.title')}</h1>
        </div>
        <p className="max-w-3xl text-sm text-muted-foreground">{t('admin.subtitle')}</p>
      </div>

      <Card className="rounded-lg">
        <CardHeader className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <KeyRound className="size-4 text-primary" />
              {t('admin.tokenTitle')}
            </CardTitle>
            <Badge variant={tokenStatus.variant}>
              {tokenStatus.loading ? <Loader2 className="size-3 animate-spin" /> : null}
              {tokenStatus.label}
            </Badge>
          </div>
          <CardDescription>{t('admin.tokenDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="admin-token">{t('admin.tokenLabel')}</Label>
            <div className="flex gap-2">
              <Input
                id="admin-token"
                value={token}
                onChange={(event) => setToken(event.target.value)}
                type={showToken ? 'text' : 'password'}
                autoComplete="off"
                placeholder={hasToken ? t('admin.tokenConfiguredPlaceholder') : t('admin.tokenPlaceholder')}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') onSave()
                }}
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => setShowToken((value) => !value)}
                aria-label={showToken ? t('admin.hideToken') : t('admin.showToken')}
              >
                {showToken ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
              </Button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" onClick={onSave}>
              <KeyRound className="size-4" />
              {t('admin.saveToken')}
            </Button>
            <Button type="button" variant="outline" onClick={onClear} disabled={!hasToken && !token}>
              <Trash2 className="size-4" />
              {t('admin.clearToken')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {!hasToken ? (
        <Card className="rounded-lg border-dashed">
          <CardHeader>
            <CardTitle className="text-base">{t('adminConsole.tokenMissingTitle')}</CardTitle>
            <CardDescription>{t('adminConsole.tokenMissingDescription')}</CardDescription>
          </CardHeader>
        </Card>
      ) : verifyQuery.isLoading || verifyQuery.isFetching ? (
        <Card className="rounded-lg border-dashed">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Loader2 className="size-4 animate-spin" />
              {t('adminConsole.tokenVerifyingTitle')}
            </CardTitle>
            <CardDescription>{t('adminConsole.tokenVerifyingDescription')}</CardDescription>
          </CardHeader>
        </Card>
      ) : !adminAccessAllowed ? (
        <Card className="rounded-lg border-destructive/50">
          <CardHeader>
            <CardTitle className="text-base text-destructive">{t('adminConsole.tokenInvalidTitle')}</CardTitle>
            <CardDescription>{t('adminConsole.tokenInvalidDescription')}</CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <Tabs defaultValue="users" className="gap-4">
          <TabsList className="flex w-full flex-wrap gap-1 group-data-[orientation=horizontal]/tabs:h-auto">
            <TabsTrigger value="users">
              <ShieldCheck className="size-4" />
              {t('adminConsole.tabs.users')}
            </TabsTrigger>
            <TabsTrigger value="usage">
              <BarChart3 className="size-4" />
              {t('adminConsole.tabs.usage')}
            </TabsTrigger>
            <TabsTrigger value="dashboard">
              <BarChart3 className="size-4" />
              {t('adminConsole.tabs.dashboard')}
            </TabsTrigger>
            <TabsTrigger value="observability">
              <Activity className="size-4" />
              {t('adminConsole.tabs.observability')}
            </TabsTrigger>
            <TabsTrigger value="credits">
              <Coins className="size-4" />
              {t('adminConsole.tabs.credits')}
            </TabsTrigger>
            <TabsTrigger value="models">
              <Settings2 className="size-4" />
              {t('adminConsole.tabs.models')}
            </TabsTrigger>
            <TabsTrigger value="storage">
              <HardDrive className="size-4" />
              {t('adminConsole.tabs.storage')}
            </TabsTrigger>
            <TabsTrigger value="backup">
              <DatabaseBackup className="size-4" />
              {t('adminConsole.tabs.backup')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="users" className="space-y-4">
            <Card className="rounded-lg">
              <CardHeader className="gap-4">
                <div>
                  <CardTitle>{t('adminConsole.users.title')}</CardTitle>
                  <CardDescription>{t('adminConsole.users.description')}</CardDescription>
                </div>
                <form className="flex flex-col gap-2 sm:flex-row" onSubmit={onSearch}>
                  <div className="relative flex-1">
                    <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      value={searchInput}
                      onChange={(event) => setSearchInput(event.target.value)}
                      className="pl-9"
                      placeholder={t('adminConsole.users.searchPlaceholder')}
                    />
                  </div>
                  <Button type="submit" variant="secondary">
                    <Search className="size-4" />
                    {t('adminConsole.users.search')}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => usersQuery.refetch()}>
                    <RefreshCw className="size-4" />
                    {t('common.retry')}
                  </Button>
                </form>
              </CardHeader>
              <CardContent className="space-y-4">
                {usersQuery.isLoading ? (
                  <div className="space-y-2">
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-16 w-full" />
                    <Skeleton className="h-16 w-full" />
                  </div>
                ) : usersQuery.isError ? (
                  <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
                    {getAdminErrorMessage(usersQuery.error, t)}
                  </div>
                ) : (
                  <div className="overflow-x-auto rounded-lg border">
                    <table className="w-full min-w-[860px] text-sm">
                      <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
                        <tr>
                          <th className="px-4 py-3 font-medium">{t('adminConsole.users.columns.username')}</th>
                          <th className="px-4 py-3 font-medium">{t('adminConsole.users.columns.displayName')}</th>
                          <th className="px-4 py-3 font-medium">{t('adminConsole.users.columns.trustLevel')}</th>
                          <th className="px-4 py-3 font-medium">{t('adminConsole.users.columns.isAdmin')}</th>
                          <th className="px-4 py-3 font-medium">{t('adminConsole.users.columns.isActive')}</th>
                          <th className="px-4 py-3 font-medium">{t('adminConsole.users.columns.createdAt')}</th>
                          <th className="px-4 py-3 text-right font-medium">{t('adminConsole.users.columns.actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.length ? (
                          users.map((user) => (
                            <tr key={user.user_id} className="border-t">
                              <td className="px-4 py-3 font-medium">{user.username || '-'}</td>
                              <td className="px-4 py-3 text-muted-foreground">{user.display_name || '-'}</td>
                              <td className="px-4 py-3">{user.trust_level}</td>
                              <td className="px-4 py-3">
                                <Badge variant={user.is_admin ? 'default' : 'outline'}>
                                  {user.is_admin ? t('adminConsole.users.yes') : t('adminConsole.users.no')}
                                </Badge>
                              </td>
                              <td className="px-4 py-3">
                                <Badge variant={user.is_active ? 'secondary' : 'destructive'}>
                                  {user.is_active ? t('adminConsole.users.active') : t('adminConsole.users.inactive')}
                                </Badge>
                              </td>
                              <td className="px-4 py-3 text-muted-foreground">{formatCreatedAt(user.created_at)}</td>
                              <td className="px-4 py-3">
                                <div className="flex justify-end gap-2">
                                  <Button type="button" size="sm" variant="outline" onClick={() => openEditDialog(user)}>
                                    <Pencil className="size-4" />
                                    {t('common.edit')}
                                  </Button>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="secondary"
                                    onClick={() => {
                                      setResetUser(user)
                                      setNewPassword('')
                                    }}
                                  >
                                    <KeyRound className="size-4" />
                                    {t('adminConsole.users.resetPassword')}
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td className="px-4 py-10 text-center text-muted-foreground" colSpan={7}>
                              {t('adminConsole.users.empty')}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-sm text-muted-foreground">{currentRange}</div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Select
                      value={String(limit)}
                      onValueChange={(value) => {
                        setLimit(Number(value))
                        setOffset(0)
                      }}
                    >
                      <SelectTrigger className="w-28">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={!hasPreviousPage}
                      onClick={() => setOffset(Math.max(0, offset - limit))}
                    >
                      {t('adminConsole.users.previous')}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={!hasNextPage}
                      onClick={() => setOffset(offset + limit)}
                    >
                      {t('adminConsole.users.next')}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="usage">
            <AdminUsageDashboard />
          </TabsContent>

          <TabsContent value="dashboard" className="space-y-4">
            <Card className="rounded-lg">
              <CardHeader>
                <CardTitle>{t('adminConsole.dashboard.title')}</CardTitle>
                <CardDescription>{t('adminConsole.dashboard.description')}</CardDescription>
              </CardHeader>
              <CardContent>
                <Button asChild>
                  <Link to="/admin/dashboard">
                    <BarChart3 className="size-4" />
                    {t('adminConsole.dashboard.open')}
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="observability">
            <AdminObservabilityPanel />
          </TabsContent>

          <TabsContent value="credits">
            <Card className="rounded-lg">
              <CardHeader>
                <CardTitle>{t('adminConsole.credits.title')}</CardTitle>
                <CardDescription>{t('adminConsole.credits.description')}</CardDescription>
              </CardHeader>
              <CardContent>
                <form className="grid gap-4 md:grid-cols-[1fr_180px] lg:grid-cols-[1fr_180px_1fr_auto]" onSubmit={onTopUp}>
                  <div className="space-y-2">
                    <Label htmlFor="topup-user-id">{t('adminConsole.credits.userId')}</Label>
                    <div className="relative">
                      <Search className="pointer-events-none absolute left-3 top-1/2 z-10 size-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        id="topup-user-id"
                        value={topUpUserId}
                        onChange={(event) => onTopUpUserInputChange(event.target.value)}
                        onFocus={() => setTopUpUserSearchOpen(true)}
                        onBlur={() => window.setTimeout(() => setTopUpUserSearchOpen(false), 120)}
                        className="pl-9"
                        placeholder={t('adminConsole.credits.userIdPlaceholder')}
                        autoComplete="off"
                      />
                      {topUpUserSearchOpen && !selectedTopUpUser && topUpUserId.trim().length >= 2 ? (
                        <div className="absolute z-20 mt-2 max-h-72 w-full overflow-auto rounded-md border bg-popover p-1 text-sm shadow-md">
                          {isTopUpUserSearchPending || topUpUserSearchQuery.isLoading || topUpUserSearchQuery.isFetching ? (
                            <div className="px-3 py-2 text-muted-foreground">{t('adminConsole.credits.userSearchLoading')}</div>
                          ) : topUpUserResults.length ? (
                            topUpUserResults.map((user) => (
                              <button
                                key={user.user_id}
                                type="button"
                                className="flex w-full flex-col items-start gap-1 rounded-sm px-3 py-2 text-left hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none"
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => onSelectTopUpUser(user)}
                              >
                                <span className="font-medium">{user.username || user.user_id}</span>
                                <span className="text-xs text-muted-foreground">
                                  {t('adminConsole.credits.userSearchMeta', {
                                    displayName: user.display_name || '-',
                                    userId: user.user_id.slice(0, 12),
                                  })}
                                </span>
                              </button>
                            ))
                          ) : (
                            <div className="px-3 py-2 text-muted-foreground">{t('adminConsole.credits.userSearchEmpty')}</div>
                          )}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="topup-amount">{t('adminConsole.credits.amount')}</Label>
                    <Input
                      id="topup-amount"
                      value={topUpAmount}
                      onChange={(event) => setTopUpAmount(event.target.value)}
                      type="number"
                      min="1"
                      step="1"
                      placeholder="100"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="topup-note">{t('adminConsole.credits.note')}</Label>
                    <Input
                      id="topup-note"
                      value={topUpNote}
                      onChange={(event) => setTopUpNote(event.target.value)}
                      placeholder={t('adminConsole.credits.notePlaceholder')}
                    />
                  </div>
                  <div className="flex items-end">
                    <Button type="submit" disabled={topUpMutation.isPending} className="w-full">
                      {topUpMutation.isPending ? <Loader2 className="size-4 animate-spin" /> : <Coins className="size-4" />}
                      {t('adminConsole.credits.submit')}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="models">
            <Card className="rounded-lg">
              <CardHeader>
                <CardTitle>{t('adminConsole.models.title')}</CardTitle>
                <CardDescription>{t('adminConsole.models.description')}</CardDescription>
              </CardHeader>
              <CardContent>
                <Button type="button" asChild>
                  <Link to="/admin/models">
                    <Settings2 className="size-4" />
                    {t('adminConsole.models.open')}
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="storage">
            <Card className="rounded-lg">
              <CardHeader>
                <CardTitle>{t('adminConsole.storage.title')}</CardTitle>
                <CardDescription>{t('adminConsole.storage.description')}</CardDescription>
              </CardHeader>
              <CardContent>
                <Button type="button" asChild>
                  <Link to="/admin/storage">
                    <HardDrive className="size-4" />
                    {t('adminConsole.storage.open')}
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="backup">
            <Card className="rounded-lg">
              <CardHeader className="gap-4">
                <div>
                  <CardTitle>{t('adminConsole.backup.title')}</CardTitle>
                  <CardDescription>{t('adminConsole.backup.description')}</CardDescription>
                </div>
                <div className="grid gap-3 lg:grid-cols-[1fr_auto_auto]">
                  <div className="grid gap-2">
                    <Label htmlFor="backup-passphrase">{t('adminConsole.backup.passphraseLabel')}</Label>
                    <Input
                      id="backup-passphrase"
                      value={passphrase}
                      onChange={(event) => setPassphrase(event.target.value)}
                      type="password"
                      autoComplete="new-password"
                      placeholder={t('adminConsole.backup.passphrasePlaceholder')}
                    />
                  </div>
                  <Button type="button" className="self-start" onClick={savePassphrase} disabled={passphraseMutation.isPending}>
                    {passphraseMutation.isPending ? <Loader2 className="size-4 animate-spin" /> : <KeyRound className="size-4" />}
                    {t('adminConsole.backup.savePassphrase')}
                  </Button>
                  <label className="inline-flex">
                    <input
                      className="sr-only"
                      type="file"
                      accept=".zip,application/zip"
                      onChange={(event) => {
                        const file = event.target.files?.[0] ?? null
                        if (file) {
                          setRestoreType(inferRestoreTypeFromFileName(file.name))
                          setRestoreUploadFile(file)
                          setRestoreTarget(null)
                          setRestoreConfirm('')
                        }
                        event.currentTarget.value = ''
                      }}
                    />
                    <Button type="button" variant="outline" asChild>
                      <span>
                        <Upload className="size-4" />
                        {t('adminConsole.backup.uploadRestore')}
                      </span>
                    </Button>
                  </label>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <Badge variant={passphraseSet ? 'default' : 'secondary'}>
                    {t('adminConsole.backup.passphraseStatus', {
                      status: passphraseSet
                        ? t('adminConsole.backup.passphraseConfigured')
                        : t('adminConsole.backup.passphraseNotConfigured'),
                    })}
                  </Badge>
                  <Badge variant={getRemoteBadgeVariant(getBackupRemoteStatus('core')?.state ?? '')}>
                    {t('adminConsole.backup.remoteStatus', {
                      type: 'core',
                      status: getRemoteBadgeLabel(getBackupRemoteStatus('core')?.state ?? ''),
                    })}
                  </Badge>
                  <Badge variant={getRemoteBadgeVariant(getBackupRemoteStatus('assets')?.state ?? '')}>
                    {t('adminConsole.backup.remoteStatus', {
                      type: 'assets',
                      status: getRemoteBadgeLabel(getBackupRemoteStatus('assets')?.state ?? ''),
                    })}
                  </Badge>
                  {allowSelfRestart ? (
                    <Button type="button" variant="outline" size="sm" onClick={() => restartMutation.mutate()} disabled={restartMutation.isPending}>
                      {restartMutation.isPending ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
                      {t('adminConsole.backup.restartNow')}
                    </Button>
                  ) : null}
                </div>
              </CardHeader>
            </Card>

            <Card className="rounded-lg">
              <CardHeader className="gap-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <CardTitle>{t('adminConsole.backup.coreTitle')}</CardTitle>
                    <CardDescription>{t('adminConsole.backup.coreDescription')}</CardDescription>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={getRemoteBadgeVariant(getBackupRemoteStatus('core')?.state ?? '')}>
                      {t('adminConsole.backup.remoteStatus', {
                        type: 'core',
                        status: getRemoteBadgeLabel(getBackupRemoteStatus('core')?.state ?? ''),
                      })}
                    </Badge>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => saveBackupConfig('core', coreConfig)}
                      disabled={savingType === 'core'}
                    >
                      {savingType === 'core' ? <Loader2 className="size-4 animate-spin" /> : <DatabaseBackup className="size-4" />}
                      {t('adminConsole.backup.saveConfig')}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => runBackup('core')}
                      disabled={runningType === 'core'}
                    >
                      {runningType === 'core' ? <Loader2 className="size-4 animate-spin" /> : <DatabaseBackup className="size-4" />}
                      {t('adminConsole.backup.run')}
                    </Button>
                  </div>
                </div>
                <div className="grid gap-3 sm:grid-cols-5">
                  <div className="flex items-center gap-2">
                    <Switch
                      id="core-enabled"
                      checked={coreConfig.enabled}
                      onCheckedChange={(checked) => setCoreConfig((prev) => ({ ...prev, enabled: checked }))}
                    />
                    <Label htmlFor="core-enabled">{t('adminConsole.backup.enabled')}</Label>
                  </div>
                  <div className="sm:col-span-2 space-y-1">
                    <Label htmlFor="core-interval">{t('adminConsole.backup.intervalLabel')}</Label>
                    <Input
                      id="core-interval"
                      value={coreConfig.interval}
                      onChange={(event) => setCoreConfig((prev) => ({ ...prev, interval: event.target.value }))}
                      placeholder={t('adminConsole.backup.coreIntervalPlaceholder')}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="core-keep-n">{t('adminConsole.backup.keepCount')}</Label>
                    <Input
                      id="core-keep-n"
                      value={coreConfig.keepN}
                      onChange={(event) => setCoreConfig((prev) => ({ ...prev, keepN: event.target.value }))}
                      type="number"
                      min="1"
                      step="1"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="core-target">{t('adminConsole.backup.targetLabel')}</Label>
                    <Select
                      value={coreConfig.target}
                      onValueChange={(value) => setCoreConfig((prev) => ({
                        ...prev,
                        target: normalizeAdminBackupTarget(value),
                      }))}
                    >
                      <SelectTrigger id="core-target">
                        <SelectValue placeholder={t('adminConsole.backup.targetPlaceholder')} />
                      </SelectTrigger>
                      <SelectContent>
                        {BACKUP_TYPE_OPTIONS.map((target) => (
                          <SelectItem key={target} value={target}>
                            {target === 'local'
                              ? t('adminConsole.backup.targets.local')
                              : t('adminConsole.backup.targets.s3')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {t('adminConsole.backup.currentStorageBackend', { backend: storageBackendLabel })}
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {coreBackupsQuery.isLoading ? (
                  <div className="space-y-2">
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-16 w-full" />
                  </div>
                ) : coreBackupsQuery.isError ? (
                  <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
                    {getAdminErrorMessage(coreBackupsQuery.error, t)}
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('adminConsole.backup.columns.name')}</TableHead>
                        <TableHead>{t('adminConsole.backup.columns.time')}</TableHead>
                        <TableHead>{t('adminConsole.backup.columns.size')}</TableHead>
                        <TableHead>{t('adminConsole.backup.columns.integrity')}</TableHead>
                        <TableHead>{t('adminConsole.backup.columns.actions')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {coreBackups.length ? (
                        coreBackups.map((item) => (
                          <TableRow key={item.path}>
                            <TableCell className="font-mono text-xs">{item.name}</TableCell>
                            <TableCell>{formatCreatedAt(item.timestamp)}</TableCell>
                            <TableCell>{formatBytes(item.size)}</TableCell>
                            <TableCell>
                              <Badge variant={item.metadata.integrity_status === 'ok' ? 'default' : 'destructive'}>
                                {item.metadata.integrity_status || t('adminConsole.backup.integrityUnknown')}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-2">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => downloadBackupMutation.mutate(item.name)}
                                  disabled={downloadBackupMutation.isPending}
                                >
                                  <Download className="size-4" />
                                  {t('adminConsole.backup.download')}
                                </Button>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setRestoreType('core')
                                    setRestoreTarget(item)
                                    setRestoreUploadFile(null)
                                    setRestoreConfirm('')
                                  }}
                                >
                                  <RefreshCw className="size-4" />
                                  {t('adminConsole.backup.restore')}
                                </Button>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => deleteBackupMutation.mutate(item.name)}
                                  disabled={deleteBackupMutation.isPending}
                                >
                                  <Trash2 className="size-4" />
                                  {t('adminConsole.backup.delete')}
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell className="py-10 text-center text-muted-foreground" colSpan={5}>
                            {t('adminConsole.backup.empty')}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            <Card className="rounded-lg">
              <CardHeader className="gap-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <CardTitle>{t('adminConsole.backup.assetsTitle')}</CardTitle>
                    <CardDescription>{t('adminConsole.backup.assetsDescription')}</CardDescription>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={isS3Storage ? 'secondary' : 'outline'}>
                      {isS3Storage
                        ? t('adminConsole.backup.assetsObjectStorage')
                        : t('adminConsole.backup.assetsLocalDirectory')}
                    </Badge>
                    <Badge variant={getRemoteBadgeVariant(getBackupRemoteStatus('assets')?.state ?? '')}>
                      {t('adminConsole.backup.remoteStatus', {
                        type: 'assets',
                        status: getRemoteBadgeLabel(getBackupRemoteStatus('assets')?.state ?? ''),
                      })}
                    </Badge>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => saveBackupConfig('assets', assetsConfig)}
                      disabled={savingType === 'assets'}
                    >
                      {savingType === 'assets' ? <Loader2 className="size-4 animate-spin" /> : <DatabaseBackup className="size-4" />}
                      {t('adminConsole.backup.saveConfig')}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => runBackup('assets')}
                      disabled={runningType === 'assets'}
                    >
                      {runningType === 'assets' ? <Loader2 className="size-4 animate-spin" /> : <DatabaseBackup className="size-4" />}
                      {t('adminConsole.backup.run')}
                    </Button>
                  </div>
                </div>
                <div className="grid gap-3 sm:grid-cols-5">
                  <div className="flex items-center gap-2">
                    <Switch
                      id="assets-enabled"
                      checked={assetsConfig.enabled}
                      onCheckedChange={(checked) => setAssetsConfig((prev) => ({ ...prev, enabled: checked }))}
                    />
                    <Label htmlFor="assets-enabled">{t('adminConsole.backup.enabled')}</Label>
                  </div>
                  <div className="sm:col-span-2 space-y-1">
                    <Label htmlFor="assets-interval">{t('adminConsole.backup.intervalLabel')}</Label>
                    <Input
                      id="assets-interval"
                      value={assetsConfig.interval}
                      onChange={(event) => setAssetsConfig((prev) => ({ ...prev, interval: event.target.value }))}
                      placeholder={t('adminConsole.backup.assetsIntervalPlaceholder')}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="assets-keep-n">{t('adminConsole.backup.keepCount')}</Label>
                    <Input
                      id="assets-keep-n"
                      value={assetsConfig.keepN}
                      onChange={(event) => setAssetsConfig((prev) => ({ ...prev, keepN: event.target.value }))}
                      type="number"
                      min="1"
                      step="1"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="assets-target">{t('adminConsole.backup.targetLabel')}</Label>
                    <Select
                      value={assetsConfig.target}
                      onValueChange={(value) => setAssetsConfig((prev) => ({
                        ...prev,
                        target: normalizeAdminBackupTarget(value),
                      }))}
                    >
                      <SelectTrigger id="assets-target">
                        <SelectValue placeholder={t('adminConsole.backup.targetPlaceholder')} />
                      </SelectTrigger>
                      <SelectContent>
                        {BACKUP_TYPE_OPTIONS.map((target) => (
                          <SelectItem key={target} value={target}>
                            {target === 'local'
                              ? t('adminConsole.backup.targets.local')
                              : t('adminConsole.backup.targets.s3')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {t('adminConsole.backup.currentStorageBackend', { backend: storageBackendLabel })}
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {assetsBackupsQuery.isLoading ? (
                  <div className="space-y-2">
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-16 w-full" />
                  </div>
                ) : assetsBackupsQuery.isError ? (
                  <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
                    {getAdminErrorMessage(assetsBackupsQuery.error, t)}
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('adminConsole.backup.columns.name')}</TableHead>
                        <TableHead>{t('adminConsole.backup.columns.time')}</TableHead>
                        <TableHead>{t('adminConsole.backup.columns.size')}</TableHead>
                        <TableHead>{t('adminConsole.backup.columns.integrity')}</TableHead>
                        <TableHead>{t('adminConsole.backup.columns.actions')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {assetsBackups.length ? (
                        assetsBackups.map((item) => (
                          <TableRow key={item.path}>
                            <TableCell className="font-mono text-xs">{item.name}</TableCell>
                            <TableCell>{formatCreatedAt(item.timestamp)}</TableCell>
                            <TableCell>{formatBytes(item.size)}</TableCell>
                            <TableCell>
                              <Badge variant={item.metadata.integrity_status === 'ok' ? 'default' : 'destructive'}>
                                {item.metadata.integrity_status || t('adminConsole.backup.integrityUnknown')}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-2">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => downloadBackupMutation.mutate(item.name)}
                                  disabled={downloadBackupMutation.isPending}
                                >
                                  <Download className="size-4" />
                                  {t('adminConsole.backup.download')}
                                </Button>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setRestoreType('assets')
                                    setRestoreTarget(item)
                                    setRestoreUploadFile(null)
                                    setRestoreConfirm('')
                                  }}
                                >
                                  <RefreshCw className="size-4" />
                                  {t('adminConsole.backup.restore')}
                                </Button>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => deleteBackupMutation.mutate(item.name)}
                                  disabled={deleteBackupMutation.isPending}
                                >
                                  <Trash2 className="size-4" />
                                  {t('adminConsole.backup.delete')}
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell className="py-10 text-center text-muted-foreground" colSpan={5}>
                            {t('adminConsole.backup.empty')}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      <Dialog
      open={Boolean(restoreTarget || restoreUploadFile)}
      onOpenChange={(open) => {
        if (!open) {
          setRestoreTarget(null)
          setRestoreType('core')
          setRestoreUploadFile(null)
          setRestoreConfirm('')
        }
      }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('adminConsole.backup.restoreTitle')}</DialogTitle>
            <DialogDescription>
              {restoreTarget?.name ?? restoreUploadFile?.name ?? t('adminConsole.backup.restoreUploadSource')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {restoreTarget ? (
              <div className="grid gap-2 rounded-lg border p-3 text-sm sm:grid-cols-3">
                <div>
                  <div className="text-muted-foreground">{t('adminConsole.backup.columns.time')}</div>
                  <div>{formatCreatedAt(restoreTarget.timestamp)}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">{t('adminConsole.backup.users')}</div>
                  <div>{restoreTarget.metadata.user_count}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">{t('adminConsole.backup.projects')}</div>
                  <div>{restoreTarget.metadata.project_count}</div>
                </div>
              </div>
            ) : null}
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
              {t('adminConsole.backup.restoreWarning')}
            </div>
            <div className="space-y-2">
              <Label htmlFor="restore-confirm">{t('adminConsole.backup.confirmLabel')}</Label>
              <Input
                id="restore-confirm"
                value={restoreConfirm}
                onChange={(event) => setRestoreConfirm(event.target.value)}
                placeholder={t('adminConsole.backup.confirmPlaceholder')}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setRestoreTarget(null)
                setRestoreUploadFile(null)
                setRestoreConfirm('')
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={restoreConfirm !== 'RESTORE' || restoreMutation.isPending}
              onClick={() => restoreMutation.mutate()}
            >
              {restoreMutation.isPending ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
              {t('adminConsole.backup.confirmRestore')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(editState)} onOpenChange={(open) => !open && setEditState(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('adminConsole.users.editTitle')}</DialogTitle>
            <DialogDescription>{editState?.user.username}</DialogDescription>
          </DialogHeader>
          {editState ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-trust-level">{t('adminConsole.users.columns.trustLevel')}</Label>
                <Input
                  id="edit-trust-level"
                  value={editState.trustLevel}
                  onChange={(event) => setEditState({ ...editState, trustLevel: event.target.value })}
                  type="number"
                  min="0"
                  step="1"
                />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <Button
                  type="button"
                  variant={editState.isAdmin ? 'default' : 'outline'}
                  onClick={() => setEditState({ ...editState, isAdmin: !editState.isAdmin })}
                >
                  {editState.isAdmin ? t('adminConsole.users.adminOn') : t('adminConsole.users.adminOff')}
                </Button>
                <Button
                  type="button"
                  variant={editState.isActive ? 'default' : 'outline'}
                  onClick={() => setEditState({ ...editState, isActive: !editState.isActive })}
                >
                  {editState.isActive ? t('adminConsole.users.activeOn') : t('adminConsole.users.activeOff')}
                </Button>
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setEditState(null)}>
              {t('common.cancel')}
            </Button>
            <Button type="button" onClick={submitEdit} disabled={updateUserMutation.isPending}>
              {updateUserMutation.isPending ? <Loader2 className="size-4 animate-spin" /> : <Pencil className="size-4" />}
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(resetUser)} onOpenChange={(open) => !open && setResetUser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('adminConsole.users.resetTitle')}</DialogTitle>
            <DialogDescription>
              {resetUser ? t('adminConsole.users.resetDescription', { username: resetUser.username }) : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="reset-password">{t('adminConsole.users.newPassword')}</Label>
            <Input
              id="reset-password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              type="password"
              autoComplete="new-password"
              placeholder={t('adminConsole.users.newPasswordPlaceholder')}
              onKeyDown={(event) => {
                if (event.key === 'Enter') submitResetPassword()
              }}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setResetUser(null)}>
              {t('common.cancel')}
            </Button>
            <Button type="button" onClick={submitResetPassword} disabled={resetPasswordMutation.isPending}>
              {resetPasswordMutation.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <KeyRound className="size-4" />
              )}
              {t('adminConsole.users.resetPassword')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
